import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  useArees,
  useCalendaris,
  useCoberturesCalendaris,
  useDepartaments,
  type CalendariDto,
  type CoberturaDto,
} from '@/api/planificacio';
import { Icona } from '@/components/Icona';
import { useFormat } from '@/i18n/useFormat';
import { Toolbar } from '@/layout/Toolbar';
import { ErrorCarrega } from '@/pages/EstatsPagina';
import {
  avui,
  dataCurta,
  deDiaClau,
  diaClau,
  diaSetmana,
  nivellCobertura,
  nomAmbit,
  rangDies,
} from './calendari';
import './planificacio.css';

const DIES = 14;
type Agrupacio = 'DEPARTAMENT' | 'AREA';

function AlertaCalendari({ c, cob, ambit }: { c: CalendariDto; cob: CoberturaDto; ambit: string }) {
  const { t } = useTranslation('planificacio');
  const f = useFormat();
  const ajustats = cob.dies.filter((d) => d.percentatge > 0 && d.percentatge < 100);
  const sense = cob.forats.length > 0;
  if (!sense && !ajustats.length) return null;
  const llistaDies = (dies: string[]) => dies.map((d) => String(deDiaClau(d).getDate())).join(', ');
  return (
    <div className="alerta-cob" data-alerta={c.id}>
      <div className="hstack gap-8">
        <Icona
          nom={sense ? 'exclamationmark-octagon' : 'exclamationmark-triangle'}
          className={`icon-18 ${sense ? 'c-red' : 'c-orange'}`}
        />
        <Link className="t-subhead strong" to={`/planificacio/calendaris/${c.id}`}>
          {c.nom}
        </Link>
      </div>
      <div className="t-caption-2 dim mt-4">{ambit}</div>
      {cob.forats.length ? (
        <div className="t-footnote dim mt-4">
          {t('cobertura.senseTitular')}{' '}
          {cob.forats.map((fr, i) => (
            <span
              key={`${fr.inici}-${i}`}
              data-forat={c.id}
              title={`${f.dataHora(fr.inici)} — ${f.dataHora(fr.fi)}`}
            >
              {i > 0 ? ', ' : ''}
              {dataCurta(fr.inici, f.idioma)}
            </span>
          ))}
        </div>
      ) : null}
      {ajustats.length ? (
        <div className="t-footnote dim mt-4">
          {t('cobertura.ajustat', {
            count: ajustats.length,
            dies: llistaDies(ajustats.map((d) => d.data)),
          })}
        </div>
      ) : null}
      <Link className="btn btn--ghost btn--xs mt-8" to={`/planificacio/calendaris/${c.id}`}>
        <Icona nom="calendar" />
        {t('cobertura.resol')}
      </Link>
    </div>
  );
}

export function W15Cobertura() {
  const { t } = useTranslation('planificacio');
  const f = useFormat();
  const calendaris = useCalendaris();
  const departaments = useDepartaments();
  const arees = useArees();
  const [agrupacio, setAgrupacio] = useState<Agrupacio>('AREA');
  const desde = diaClau(avui());
  const llista = useMemo(
    () =>
      (calendaris.data ?? []).filter((c) =>
        agrupacio === 'AREA' ? c.areaId != null : c.departamentId != null && c.areaId == null,
      ),
    [calendaris.data, agrupacio],
  );
  const totsIds = (calendaris.data ?? []).map((c) => c.id);
  const cobertures = useCoberturesCalendaris(totsIds, desde, DIES);
  const cob = (c: CalendariDto) => cobertures[totsIds.indexOf(c.id)]?.data ?? null;
  const dies = rangDies(avui(), DIES);

  const amb = llista.map((c) => ({ c, cob: cob(c) }));
  const ambit = (c: CalendariDto) => nomAmbit(c, departaments.data ?? [], arees.data ?? []);
  const alertes = amb.filter(
    (x) => x.cob && (x.cob.forats.length || x.cob.dies.some((d) => d.percentatge < 100)),
  );
  const ordenades = [...alertes].sort(
    (a, b) => (b.cob?.forats.length ?? 0) - (a.cob?.forats.length ?? 0),
  );

  return (
    <section className="pantalla" data-screen="W-15">
      <Toolbar titol={t('titols.cobertura')}>
        <div className="segmented" style={{ width: 260 }}>
          {(['DEPARTAMENT', 'AREA'] as const).map((a) => (
            <button
              key={a}
              type="button"
              style={{ flex: 1 }}
              aria-pressed={agrupacio === a}
              onClick={() => setAgrupacio(a)}
            >
              {t(`cobertura.per.${a}`)}
            </button>
          ))}
        </div>
      </Toolbar>
      <div className="content cobertura">
        <div className="panel">
          <div className="panel__head">
            <h3>{t('cobertura.mapa', { dies: DIES })}</h3>
            <div className="spacer" />
            <div className="legend">
              {(['COBERT', 'AJUSTAT', 'SENSE'] as const).map((n) => (
                <span key={n}>
                  <i className={`heatmap__cel--${n}`} />
                  {t(`nivellCobertura.${n}`)}
                </span>
              ))}
            </div>
          </div>
          {calendaris.isError ? (
            <ErrorCarrega onReintenta={() => void calendaris.refetch()} />
          ) : llista.length === 0 ? (
            <div className="empty">
              <Icona nom="shield-check" />
              {calendaris.isLoading ? t('comu.carregant') : t('cobertura.buit')}
            </div>
          ) : (
            <div className="panel__body graella-scroll">
              <table className="heatmap">
                <thead>
                  <tr>
                    <th>{t('cobertura.ambit')}</th>
                    {dies.map((d) => (
                      <th key={diaClau(d)} title={t(`diesLlargs.${diaSetmana(d)}`)}>
                        {String(d.getDate()).padStart(2, '0')}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {amb.map(({ c, cob: cb }) => (
                    <tr key={c.id} data-calendari={c.id}>
                      <th>
                        <Link to={`/planificacio/calendaris/${c.id}`}>{c.nom}</Link>
                      </th>
                      {dies.map((d) => {
                        const dia = cb?.dies.find((x) => x.data === diaClau(d));
                        const nivell = dia ? nivellCobertura(dia.percentatge) : null;
                        return (
                          <td
                            key={diaClau(d)}
                            className={`heatmap__cel heatmap__cel--${nivell ?? 'ND'}`}
                            data-nivell={nivell ?? 'ND'}
                            title={
                              dia
                                ? `${f.data(deDiaClau(dia.data))} · ${f.percentatge(dia.percentatge)}`
                                : ''
                            }
                          />
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="panel">
          <div className="panel__head">
            <h3>{t('cobertura.alertes', { count: ordenades.length })}</h3>
          </div>
          <div className="panel__body">
            {ordenades.length === 0 ? (
              <div className="empty">
                <Icona nom="checkmark-circle" />
                {t('cobertura.senseAlertes')}
              </div>
            ) : (
              <div className="stack stack--s">
                {ordenades.map(({ c, cob: cb }) =>
                  cb ? <AlertaCalendari key={c.id} c={c} cob={cb} ambit={ambit(c)} /> : null,
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
